import CubemapUtil from 'lib/helpers/cubemap.util';
import Pannellum from "lib/pannellum";
import FloorplanManager from "lib/service/e3.plan.manager";
import {getPolygonBounding} from "a_common-folder/utils/geometry";
import { generateUUID } from 'lib/helpers/common';

export default class BlurplanManager {
    _vertices = [];
    _rectangleVertices = [];

    constructor() {
    }

    /**
     * Update vertex spherical and cubeBox coordinates for rendering
     * @param {number[]} spherCoords
     * @param {number} index
     * @param {boolean} adjustMode
     * @param {boolean} isFloor
     * @param {boolean} updateProps
     */
    updateVertexView(spherCoords, index, adjustMode, isFloor = true, updateProps = false) {
        if (!updateProps) {
            spherCoords = spherCoords.map(v => v * Pannellum.RADIAN);
        }

        let vertex = this._vertices[index];
        vertex.sphere = [FloorplanManager.normalizeYaw(spherCoords[0]), spherCoords[1]];
        vertex.floorCube = CubemapUtil.getBoxCoords(...spherCoords);
    }

    /**
     * Remove snapData property of vertex object
     * @param {number} index
     */
    removeVertexSnapData(index) {
        let vertex = this._vertices[index];
        if (vertex.snapData) {
            delete vertex.snapData;
        }
    }

    /**
     * Add point to current area
     * @param {number[]} spherCoord - pair of yaw and pitch
     */
    addPoint(spherCoord) {
        let pair;

        if (this._ghostCubeCoord) {
            pair = this._ghostCubeCoord.spherCoords;
            this._vertices.push({
                sphere: pair[0],
                floorCube: CubemapUtil.getBoxCoords(...pair[0]),
                coord2d: FloorplanManager.sphereTo2dCorrected(...pair[0], {}),
            });
        }
    }

    getBountingCoord(vertices){
        return getPolygonBounding(vertices);
    }

    removeCurrentBlurArea(){
        this._vertices = [];
    }

    removeRectangle(uuid){
        this._rectangleVertices = this._rectangleVertices.filter((rectangle) => rectangle.uuid !== uuid);
    }

    isBlurred(uuid){
        const rectangle = this._rectangleVertices.filter((rectangle) => rectangle.uuid === uuid)[0];

        return rectangle.blurred;
    }

    pushToRectangleVertices(uuid, hasGhostPoint = true, finished = false){
        let vertices = this._vertices.map(data => [data.floorCube]);
        let sphereCoords = this._vertices.map(data => data.sphere);
        let retObj = {hasGhostPoint: hasGhostPoint, finished: finished};
        retObj.points = vertices;
        retObj.sphereCoords = sphereCoords;
        // retObj.rectanglePoints = vertices;
        retObj.uuid = uuid;
        const {maxX, maxY} = this.getBountingCoord(sphereCoords);

        retObj.bountingCoord = [[FloorplanManager.normalizeYaw(maxX), maxY]];

        this._rectangleVertices.push(retObj);
        this._vertices = [];

        return retObj;
    }

    countRectangle(){
        return this._rectangleVertices.length;
    }

    /**
     * Get cubeBox coordinates for each vertex in _vertices array.
     * Needed for drawing points by webGL on cube faces
     * @returns {{points: Array, hasGhostPoint: boolean, finished: *}}
     */
    getCubeVertices() {
        let retObj = {hasGhostPoint: !!this._ghostCubeCoord, finished: this.isDrawingFinished()};

        let vertices = this._vertices.map(data => [data.floorCube]);
        if (this._ghostCubeCoord) {
            vertices.push(this._ghostCubeCoord.coords);
        }

        retObj.hasGhostPoint = true;
        retObj.finished = false;
        retObj.points = vertices;
        retObj.rectanglePoints = this._rectangleVertices;

        return retObj;
    }

    /**
     * Return quantity of vertices in current area
     * @returns {number}
     */
    getVertexCount() {
        return this._vertices.length;
    }

    /**
     * Return quantity of vertices in current area
     * @returns {number}
     */
    getBountingCoordCount() {
        return this._rectangleVertices.length;
    }

    /**
     * Return spherical coordinates of current vertices
     * @returns {Array}
     */
    getVerticesSpherCoords() {
        return this._vertices.map(data => [data.sphere]);
    }

    /**
     * Return spherical coordinates of current vertices
     * @returns {Array}
     */
    getBountingCoordVerticesSpherCoords() {
        const rectangles = this._rectangleVertices.map(({bountingCoord, uuid}) => {
            return {bountingCoord, uuid}
        })

        return rectangles;
    }

    /**
     * set process of drawing blurplan is finished
     * @returns {boolean}
     */
    setDrawingFinished(drawing) {
        this.isDrawing = drawing;

        return this.isDrawing
    }

    /**
     * get rectangle data
     * @returns {obj}
     */
    getRectangleVertices() {
        return this._rectangleVertices;
    }

    /**
     * get rectangle data
     * @returns {obj}
     */
    getBlurPolylines() {
        return this._rectangleVertices;
    }

    /**
     * get rectangle data
     * @returns {obj}
     */
    getBlurPolylinesQuery() {
        return this._rectangleVertices.map((rectangleVertice) => {
            const coord2ds = rectangleVertice.sphereCoords.map((sphereCoord) => FloorplanManager.sphereTo2dCorrected(...sphereCoord, {}))

            return coord2ds.map((coord2d) => `&pol=${coord2d[0]}&pol=${coord2d[1]}`)
        }).flat().join('');
    }

  /**
     * get rectangle data
     * @returns {obj}
     */
    getBlur2dPolylines() {
        return this._rectangleVertices.map(({sphereCoords}) => { return {sphereCoords}});
    }
  /**
     * get rectangle data
     * @returns {obj}
     */
  getBlurCartCoord() {
        return this._rectangleVertices.map(({sphereCoords}) => {
            return sphereCoords.map((sphereCoord) => {
                return {yaw: sphereCoord[0], pitch: sphereCoord[1]}
            })
        });
    }

    /**
     * set rectangle data
     * @returns {boolean}
     */
    setRectangleVertices(rectangleVertices) {
        this._rectangleVertices = [];

        rectangleVertices.forEach(({sphereCoords}) => {
            const {maxX, maxY} = this.getBountingCoord(sphereCoords);

            this._rectangleVertices.push({
                hasGhostPoint: false,
                finished: true,
                blurred: true,
                uuid: generateUUID(),
                sphereCoords: sphereCoords,
                points: sphereCoords.map((sphereCoord) => [CubemapUtil.getBoxCoords(...sphereCoord)]),
                bountingCoord: [[FloorplanManager.normalizeYaw(maxX), maxY]],
            });
        })

        return true;
    }

    /**
     * Does process of drawing blurplan is finished
     * @returns {boolean}
     */
    isDrawingFinished() {
        return this.isDrawing;
    }

    /**
     * Remove ghost (not drown, cursor position) point
     */
    removeGhostPoint() {
        this._ghostCubeCoord = null;
    }

    // todo: deep review is needed
    /**
     * Calculate vertices pair for ghost (cursor pointer)
     * @param {Array<number>} coord
     * @param {number} coord[0] - yaw
     * @param {number} coord[1] - pitch
     */
    setGhostPoint(coord) {
        let pair =[[...coord.map(c => c * Pannellum.RADIAN)]]
        this._ghostCubeCoord = {};

        this._ghostCubeCoord.spherCoords = pair;
        this._ghostCubeCoord.coords = [CubemapUtil.getBoxCoords(...pair[0])];

        return pair;
    }

    destroy() {
        this._blurplan = null;
        this._vertices = [];
        this._rectangleVertices = [];
        this._ghostCubeCoord = {};
    }
}
